import axios from 'axios';
import querystring from 'query-string';
import isNull from 'lodash/isNull';

export const BUILDINGS_URL = '/buildings/buildings.json';
export const SEARCH_MANAGEMENT_COMPANIES_URL = '/buildings/search/company-name.json';
export const BUILDING_ADDRESS_OR_NAME_URL = '/buildings/search/address-or-name.json';
export const OWNER_OFFICE_URL = '/buildings/search/owner.json';
export const COMPANY_NAME_SEARCH_URL = '/buildings/search/company-name.json';
export const buildingUrl = id => `/buildings/${id}.json`;
export const listingsUrl = id => `/buildings/${id}/listings.json`;
export const buildingInaccuracyUrl = id => `/buildings/${id}/inaccuracy.json`;
export const buildingAdditionsUrl = id => `/buildings/${id}/additions.json`;
export const buildingMediaUrl = id => `/buildings/${id}/media.json`;
export const documentUrl = id => `/buildings/${id}/documents.json`;
export const deleteDocumentUrl = (buildingId, documentId) => `/buildings/${buildingId}/documents/${documentId}.json`;
export const reportInaccuracyUrl = buildingId => `/buildings/${buildingId}/inaccuracy.json`;
export const deletePhotoUrl = (buildingId, photoId) => `/buildings/${buildingId}/media/${photoId}.json`;
export const orderUrl = id => `/buildings/${id}/media/order.json`;
export const mediaGroupsIndexUrl = buildingId => `/api/internal/buildings/${buildingId}/media-groups/`;
export const mediaGroupUrl = (buildingId, mediaGroupId) => `${mediaGroupsIndexUrl(buildingId)}${mediaGroupId}/`;
export const mediaGroupMediaIndexUrl = (buildingId, mediaGroupId) => `${mediaGroupUrl(buildingId, mediaGroupId)}media/`;
export const mediaGroupMediaUrl = (buildingId, mediaGroupId, mediaId) =>
    `${mediaGroupMediaIndexUrl(buildingId, mediaGroupId)}${mediaId}/`;

const axiosCSRF = axios.create({
    xsrfCookieName: 'csrftoken',
    xsrfHeaderName: 'X-CSRFToken',
});

const getBuildings = data => {
    return axios({
        url: BUILDINGS_URL,
        params: data,
        paramsSerializer: function(params) {
            return querystring.stringify(params);
        },
    });
};

const getMediaGroups = buildingId => {
    return axios({
        url: mediaGroupsIndexUrl(buildingId),
    });
};

const fetchCompanyNotes = id => {
    return axios.get(`/companies/${id}.json`).then(resp => resp.data);
};

const setCompanyNotes = (id, data) => {
    return axiosCSRF({
        url: `/companies/${id}/additions.json`,
        method: 'PUT',
        data,
    });
};

const postMediaGroup = (buildingId, data) => {
    return axiosCSRF({
        url: mediaGroupsIndexUrl(buildingId),
        method: 'POST',
        data: data,
    });
};

const putMediaGroup = (buildingId, data) => {
    return axiosCSRF({
        url: mediaGroupUrl(buildingId, data.id),
        method: 'PUT',
        data: data,
    });
};

const deleteMediaGroup = (buildingId, mediaGroupId) => {
    return axiosCSRF({
        url: mediaGroupUrl(buildingId, mediaGroupId),
        method: 'DELETE',
    });
};

const postPhoto = (buildingId, mediaGroupId, file) => {
    const formData = new FormData();
    formData.append('media', file);
    formData.append('media_type', 10);

    return axiosCSRF({
        url: mediaGroupMediaIndexUrl(buildingId, mediaGroupId),
        method: 'POST',
        data: formData,
        responseType: 'json',
    });
};

const deleteMediaPhoto = (buildingId, mediaGroupId, mediaId) => {
    return axiosCSRF({
        url: mediaGroupMediaUrl(buildingId, mediaGroupId, mediaId),
        method: 'DELETE',
    });
};

const getListings = (id, fetchParams) => {
    return axios({
        url: listingsUrl(id),
        params: fetchParams,
        paramsSerializer: function(params) {
            return querystring.stringify(params);
        },
    });
};

const postBuildingInaccuracy = (id, data) => {
    return axiosCSRF({
        url: buildingInaccuracyUrl(id),
        method: 'POST',
        data: data,
    }).then(resp => resp.data);
};

const putBuildingAdditions = (id, data) => {
    return axiosCSRF({
        url: buildingAdditionsUrl(id),
        method: 'PUT',
        data: data,
    }).then(resp => resp.data);
};

const getBuilding = id => {
    return axios.get(buildingUrl(id));
};

const putBuilding = data => {
    return axiosCSRF({
        url: buildingUrl(data.id),
        method: 'PUT',
        data: data,
    });
};

const postMedia = (buildingId, file, photoOrder, fileType) => {
    const formData = new FormData();
    formData.append('media', file);
    formData.append('media_type', fileType);
    if (!isNull(photoOrder)) {
        formData.append('order', photoOrder);
    }

    return axiosCSRF({
        url: buildingMediaUrl(buildingId),
        method: 'POST',
        data: formData,
        responseType: 'json',
    });
};

const postDocument = (buildingId, file) => {
    const formData = new FormData();
    formData.append('document', file);
    formData.append('document_type', file.document_type);

    return axiosCSRF({
        url: documentUrl(buildingId),
        method: 'POST',
        data: formData,
    });
};

const deleteDocument = (buildingId, documentId) => {
    return axiosCSRF({
        url: deleteDocumentUrl(buildingId, documentId),
        method: 'DELETE',
    });
};

const deletePhoto = (buildingId, photoId) => {
    return axiosCSRF({
        url: deletePhotoUrl(buildingId, photoId),
        method: 'DELETE',
    });
};

const putOrder = (buildingId, order) => {
    return axiosCSRF({
        url: orderUrl(buildingId),
        method: 'PUT',
        data: { photo_order: order },
    });
};

const reportInaccuracy = data => {
    return axiosCSRF({
        url: reportInaccuracyUrl(data.model.id),
        method: 'POST',
        data: data,
    });
};

const getManagementCompanies = search => {
    const urlParams = querystring.stringify(search);
    return axios.get(SEARCH_MANAGEMENT_COMPANIES_URL + '?' + urlParams).then(response => {
        return response.data.companies || [];
    });
};

const getOwnerOffice = search => {
    return axios({
        url: OWNER_OFFICE_URL,
        params: search,
    }).then(response => {
        return response.data.owners;
    });
};

const getListingCompanies = search => {
    return axios({
        url: COMPANY_NAME_SEARCH_URL,
        params: search,
        paramsSerializer: p => querystring.stringify(p),
    }).then(response => {
        return response.data.companies;
    });
};

const getBuildingsByNameOrAddress = search => {
    const urlParams = querystring.stringify(search);
    return axios.get(BUILDING_ADDRESS_OR_NAME_URL + '?' + urlParams).then(response => {
        return response.data || [];
    });
};

export default {
    getBuildings,
    getManagementCompanies,
    getOwnerOffice,
    getListingCompanies,
    getBuildingsByNameOrAddress,
    getBuilding,
    getListings,
    postBuildingInaccuracy,
    putBuildingAdditions,
    putBuilding,
    postMedia,
    postDocument,
    deleteDocument,
    deletePhoto,
    putOrder,
    reportInaccuracy,
    getMediaGroups,
    postMediaGroup,
    putMediaGroup,
    deleteMediaGroup,
    postPhoto,
    deleteMediaPhoto,
    fetchCompanyNotes,
    setCompanyNotes,
};
